import fileDownload from "js-file-download";
import {
    ImportRejection,
    ImportRejectionRowProblem,
} from "@common/schemas/domains/accounting/import-rejection.schema";
import { highlightXlsxRows } from "@common/helpers/highlightXlsxRows";
import { collectImportRejectionFlaggedRows } from "@common/helpers/importRejectionRowNumbers";
import {
    XLSX_MIME,
    readBlobResponseAsArrayBuffer,
    useFileDownload,
} from "@lib/fileDownload";
import { getFinancialDocumentFile } from "@lib/financialDocument";

export function flaggedRowNumbers(rejection: ImportRejection): number[] {
    if (rejection.rowNumbers != null && rejection.rowNumbers.length > 0) {
        return rejection.rowNumbers;
    }
    return (rejection.exampleRows ?? []).map((row) => row.rowNumber);
}

export function flaggedRowProblems(
    rejection: ImportRejection,
): ImportRejectionRowProblem[] {
    if (rejection.rowProblems != null && rejection.rowProblems.length > 0) {
        return rejection.rowProblems;
    }
    const exampleRows = rejection.exampleRows ?? [];
    const exampleRowNumbers = new Set(
        exampleRows.map((exampleRow) => exampleRow.rowNumber),
    );
    return (
        collectImportRejectionFlaggedRows([
            ...exampleRows.map((exampleRow) => ({
                rowNumber: exampleRow.rowNumber,
                problem: exampleRow.problem,
            })),
            ...flaggedRowNumbers(rejection)
                .filter((rowNumber) => !exampleRowNumbers.has(rowNumber))
                .map((rowNumber) => ({
                    rowNumber,
                    problem: rejection.summary,
                })),
        ]).rowProblems ?? []
    );
}

export function flaggedRowsDownloadFilename(fileName: string): string {
    const baseName = fileName.replace(/\.[^./]+$/, "") || "export";
    return `${baseName}-flagged-rows.xlsx`;
}

function uint8ArrayToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    const copy = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(copy).set(bytes);
    return copy;
}

interface DownloadFlaggedRowsParams {
    documentId?: number;
    fileName?: string;
    rowNumbers: number[];
    rowProblems: ImportRejectionRowProblem[];
}

export function useDownloadFlaggedRows({
    documentId,
    fileName,
    rowNumbers,
    rowProblems,
}: DownloadFlaggedRowsParams) {
    return useFileDownload(async () => {
        if (documentId == null || rowNumbers.length === 0) {
            return;
        }
        const response = await getFinancialDocumentFile(documentId);
        const highlighted = highlightXlsxRows({
            workbookBytes: await readBlobResponseAsArrayBuffer(response),
            rowNumbers,
            rowProblems,
        });
        fileDownload(
            uint8ArrayToArrayBuffer(highlighted),
            flaggedRowsDownloadFilename(fileName ?? "export.xlsx"),
            XLSX_MIME,
        );
    });
}
